import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { DollarSign, Save, TrendingDown, TrendingUp, Info } from "lucide-react";
import { toast } from "sonner";

export default function AdminPricing() {
  const [data, setData] = useState(null);
  const [programs, setPrograms] = useState({});
  const [subs, setSubs] = useState({});
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await api.get("/admin/pricing");
    setData(data);
    setPrograms(Object.fromEntries(data.programs.map((p) => [p.id, { amount: p.amount, alumni_amount: p.alumni_amount }])));
    setSubs(Object.fromEntries(data.subscriptions.map((s) => [s.tier, s.price])));
  };
  useEffect(() => { load().catch(() => toast.error("Failed to load pricing")); }, []);

  const saveProgram = async (pid) => {
    setSaving(true);
    try {
      await api.patch("/admin/pricing", { programs: { [pid]: programs[pid] } });
      toast.success("Program price updated for everyone.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const saveSub = async (tier) => {
    const current = data.subscriptions.find((s) => s.tier === tier)?.price;
    const next = Number(subs[tier]);
    if (next === current) { toast("No change to save"); return; }
    const raising = next > current;
    const msg = raising
      ? `Raise ${tier} to $${next}/mo? Current subscribers keep their price and will be emailed a 30-day notice before it changes. New subscribers pay the new price immediately.`
      : `Lower ${tier} to $${next}/mo? Current subscribers move to the lower price at their next renewal.`;
    if (!window.confirm(msg)) return;
    setSaving(true);
    try {
      const { data: res } = await api.patch("/admin/pricing", { subscriptions: { [tier]: next } });
      const r = res.results.subscriptions[0];
      toast.success(r?.lowered ? "Price lowered — subscribers notified." : `Price raised — ${r?.affected_subscribers || 0} subscriber(s) get a ${r?.notice_days}-day notice.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="p-10 text-zinc-500">Loading…</div>;

  return (
    <div className="p-6 md:p-10 space-y-8" data-testid="admin-pricing-page">
      <header>
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold">Revenue</p>
        <h1 className="font-display text-3xl text-white uppercase tracking-tight">Pricing</h1>
        <p className="text-sm text-zinc-400 mt-1">Change what you charge for programs and coaching subscriptions — no code required.</p>
      </header>

      {/* Programs */}
      <section>
        <h2 className="font-display uppercase tracking-widest text-sm text-white mb-3">Programs & Sessions</h2>
        <div className="space-y-3">
          {data.programs.map((p) => (
            <div key={p.id} className="border border-[#27272A] bg-[#0F0F0F] p-5 flex flex-wrap items-end gap-4" data-testid={`pricing-program-${p.id}`}>
              <div className="flex-1 min-w-[200px]">
                <div className="font-semibold text-white">{p.name}</div>
                <div className="text-xs text-zinc-500 uppercase tracking-widest">{p.type}</div>
              </div>
              <Field label="Price ($)" value={programs[p.id]?.amount ?? ""} testid={`price-${p.id}`}
                onChange={(v) => setPrograms((s) => ({ ...s, [p.id]: { ...s[p.id], amount: v } }))} />
              {p.alumni_amount !== null && (
                <Field label="Alumni ($)" value={programs[p.id]?.alumni_amount ?? ""} testid={`alumni-${p.id}`}
                  onChange={(v) => setPrograms((s) => ({ ...s, [p.id]: { ...s[p.id], alumni_amount: v } }))} />
              )}
              <Button onClick={() => saveProgram(p.id)} disabled={saving} data-testid={`save-program-${p.id}`}
                className="bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest text-xs font-bold">
                <Save className="h-4 w-4 mr-2" /> Save
              </Button>
            </div>
          ))}
        </div>
        <p className="text-xs text-zinc-500 mt-2 flex items-center gap-1.5"><Info className="h-3.5 w-3.5" /> Program & session price changes take effect instantly for everyone.</p>
      </section>

      {/* Subscriptions */}
      <section>
        <h2 className="font-display uppercase tracking-widest text-sm text-white mb-3">Coaching Subscriptions</h2>
        <div className="space-y-3">
          {data.subscriptions.map((s) => {
            const changed = Number(subs[s.tier]) !== s.price;
            const raising = Number(subs[s.tier]) > s.price;
            return (
              <div key={s.tier} className="border border-[#27272A] bg-[#0F0F0F] p-5 flex flex-wrap items-end gap-4" data-testid={`pricing-sub-${s.tier}`}>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-semibold text-white">{s.name}</div>
                  <div className="text-xs text-zinc-500">Currently ${s.price.toFixed(2)}/mo</div>
                </div>
                <Field label="Price ($/mo)" value={subs[s.tier] ?? ""} testid={`price-sub-${s.tier}`}
                  onChange={(v) => setSubs((x) => ({ ...x, [s.tier]: v }))} />
                {changed && (
                  <span className={`text-[10px] font-bold uppercase tracking-widest flex items-center gap-1 ${raising ? "text-amber-400" : "text-emerald-400"}`}>
                    {raising ? <><TrendingUp className="h-3.5 w-3.5" /> 30-day notice</> : <><TrendingDown className="h-3.5 w-3.5" /> applies next renewal</>}
                  </span>
                )}
                <Button onClick={() => saveSub(s.tier)} disabled={saving || !changed} data-testid={`save-sub-${s.tier}`}
                  className="bg-[#FF331F] hover:bg-[#FF5242] text-white uppercase tracking-widest text-xs font-bold">
                  <Save className="h-4 w-4 mr-2" /> Save
                </Button>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-zinc-500 mt-2 flex items-center gap-1.5"><Info className="h-3.5 w-3.5" /> Lowering a price moves current subscribers down at their next renewal. Raising keeps them at their current price and emails them a {data.price_raise_notice_days}-day countdown before the new price applies. New subscribers always pay the current price.</p>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold block mb-1">{label}</label>
      <div className="relative">
        <DollarSign className="h-4 w-4 text-zinc-600 absolute left-2.5 top-1/2 -translate-y-1/2" />
        <Input type="number" min="0" step="1" value={value} onChange={(e) => onChange(e.target.value)}
          data-testid={testid} className="w-32 pl-8 bg-black border-[#27272A] text-white" />
      </div>
    </div>
  );
}
